<template>
  <v-container>
    <h2 class="mb-4">
      {{ $t("featuredBookManager.header") }}
    </h2>

    <div class="mb-6">
      <v-autocomplete
        v-model="selectedBook"
        :items="bookResults"
        :loading="isSearching"
        :search-input.sync="bookSearch"
        hide-no-data
        hide-selected
        item-text="title"
        item-value="id"
        :label="$t('featuredBookManager.searchPlaceholder')"
        :placeholder="$t('featuredBookManager.searchPlaceholder')"
        prepend-icon="mdi-magnify"
        return-object
        clearable
        :filter="() => true"
        @change="addFeaturedBook"
      >
        <template #item="{ item }">
          <v-list-item-avatar tile>
            <book-cover-image
              :src="item.thumbnailUrl"
              :alt="$t('bookInfo.bookCoverAlt', { title: item.title })"
              max-width="40"
            />
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title v-text="item.title" />
          </v-list-item-content>
        </template>
      </v-autocomplete>
    </div>

    <v-alert
      v-if="!featuredBooks.length && !loading"
      type="info"
      text
    >
      {{ $t("featuredBookManager.noFeaturedBooks") }}
    </v-alert>

    <v-progress-linear
      v-if="loading"
      indeterminate
      class="mb-4"
    />

    <draggable
      v-model="featuredBooks"
      handle=".drag-handle"
      @end="onDragEnd"
    >
      <v-row
        v-for="(fb, index) in featuredBooks"
        :key="fb.id"
        class="featured-book-row mb-2 align-center"
      >
        <v-col
          cols="auto"
          class="drag-handle"
          style="cursor: grab;"
        >
          <v-icon>mdi-drag</v-icon>
        </v-col>
        <v-col cols="auto">
          <v-avatar
            tile
            size="48"
          >
            <book-cover-image
              :src="fb.book.thumbnailUrl"
              :alt="$t('bookInfo.bookCoverAlt', { title: fb.book.title })"
            />
          </v-avatar>
        </v-col>
        <v-col>
          <div class="font-weight-medium">
            {{ fb.book.title }}
          </div>
        </v-col>
        <v-col cols="auto">
          <v-chip
            small
            color="primary"
            text-color="white"
          >
            {{ index + 1 }}
          </v-chip>
        </v-col>
        <v-col cols="auto">
          <v-btn
            icon
            small
            @click="removeFeaturedBook(fb)"
          >
            <v-icon color="error">
              mdi-close
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </draggable>

    <v-btn
      v-if="orderDirty"
      color="primary"
      class="mt-4"
      :loading="saving"
      @click="saveOrder"
    >
      {{ $t("featuredBookManager.saveOrder") }}
    </v-btn>
  </v-container>
</template>

<script>
import BookCoverImage from "@/components/BookCoverImage";
import draggable from "vuedraggable";
import { debounce } from "lodash";
import FeaturedBookQueries from "@/graphql/FeaturedBookQueries";
import FeaturedBookMutations from "@/graphql/FeaturedBookMutations";
import LocalBookQueries from "@/graphql/LocalBookQueries";
import IRIS from "@/iris";

export default {
  name: "FeaturedBookManager",
  components: { BookCoverImage, draggable },
  data() {
    return {
      featuredBooks: [],
      bookSearch: "",
      bookResults: [],
      selectedBook: null,
      isSearching: false,
      loading: false,
      saving: false,
      orderDirty: false,
      searchDebounced: null,
    };
  },
  watch: {
    bookSearch(val) {
      if (val && val.length >= 2) {
        this.isSearching = true;
        this.searchDebounced(val);
      } else {
        this.bookResults = [];
      }
    },
  },
  created() {
    this.searchDebounced = debounce(this.performBookSearch, 500);
    this.fetchFeaturedBooks();
  },
  methods: {
    async fetchFeaturedBooks() {
      this.loading = true;
      try {
        const result = await this.$apollo.query({
          query: FeaturedBookQueries.getFeaturedBooks,
          variables: {
            order: [{ position: "ASC" }],
          },
          fetchPolicy: "network-only",
        });
        this.featuredBooks = result.data.featuredBooks || [];
      } catch (error) {
        console.error("Error fetching featured books:", error);
      } finally {
        this.loading = false;
      }
    },

    async performBookSearch(searchTerm) {
      try {
        const result = await this.$apollo.query({
          query: LocalBookQueries.searchLocalBooks,
          variables: {
            simplesearch: searchTerm,
            itemsPerPage: 10,
            page: 1,
          },
        });
        this.bookResults = result.data.localBooks.collection || [];
      } catch (error) {
        console.error("Error searching books:", error);
      } finally {
        this.isSearching = false;
      }
    },

    async addFeaturedBook(book) {
      if (!book) return;

      const alreadyFeatured = this.featuredBooks.some(
        (fb) => fb.book._id === book._id || fb.book.id === book.id
      );
      if (alreadyFeatured) {
        this.$store.dispatch("snackbar/showSnackbar", {
          text: this.$t("featuredBookManager.bookAlreadyFeatured"),
          color: "warning",
        });
        this.selectedBook = null;
        this.bookSearch = "";
        return;
      }

      try {
        await this.$apollo.mutate({
          mutation: FeaturedBookMutations.createFeaturedBook,
          variables: {
            input: {
              book: IRIS.localBook + book._id,
              position: this.featuredBooks.length,
            },
          },
        });
        this.$store.dispatch("snackbar/showSnackbar", {
          text: this.$t("featuredBookManager.bookAdded"),
          color: "success",
        });
        await this.fetchFeaturedBooks();
      } catch (error) {
        console.error("Error adding featured book:", error);
        this.$store.dispatch("snackbar/showSnackbar", {
          text: error.message,
          color: "error",
        });
      } finally {
        this.selectedBook = null;
        this.bookSearch = "";
      }
    },

    async removeFeaturedBook(fb) {
      try {
        await this.$apollo.mutate({
          mutation: FeaturedBookMutations.deleteFeaturedBook,
          variables: { id: fb.id },
        });
        this.$store.dispatch("snackbar/showSnackbar", {
          text: this.$t("featuredBookManager.bookRemoved"),
          color: "success",
        });
        await this.fetchFeaturedBooks();
        this.orderDirty = false;
      } catch (error) {
        console.error("Error removing featured book:", error);
        this.$store.dispatch("snackbar/showSnackbar", {
          text: error.message,
          color: "error",
        });
      }
    },

    onDragEnd() {
      this.orderDirty = true;
    },

    async saveOrder() {
      this.saving = true;
      try {
        const orderedIds = this.featuredBooks.map((fb) => fb._id);
        await this.$apollo.mutate({
          mutation: FeaturedBookMutations.reorderFeaturedBooks,
          variables: {
            input: {
              orderedIds,
            },
          },
        });
        this.$store.dispatch("snackbar/showSnackbar", {
          text: this.$t("featuredBookManager.orderSaved"),
          color: "success",
        });
        this.orderDirty = false;
        await this.fetchFeaturedBooks();
      } catch (error) {
        console.error("Error saving order:", error);
        this.$store.dispatch("snackbar/showSnackbar", {
          text: error.message,
          color: "error",
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.featured-book-row {
  background-color: var(--v-lightestAccent-base);
  border-radius: 4px;
}
</style>
